import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
  Chip,
  IconButton,
  DialogTitle,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Savings as SavingsIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  ContentCopy as CopyIcon,
  QrCode as QrCodeIcon,
  Phone as PhoneIcon,
  Hotel as HotelIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FirebaseContext, api } from 'common';
import { useTranslation } from 'react-i18next';
import GoogleMapsAutoComplete from '../components/GoogleMapsAutoComplete';
import { QRCodeSVG } from 'qrcode.react';
import turprisTranslations from '../../../turpris/turpris-translations.json';

// User type configuration
const USER_TYPES = {
  customer: {
    label: 'Passasjer',
    description: 'Sammenlign priser og book reiser',
    icon: PersonIcon,
    color: '#6366f1',
  },
  driver: {
    label: 'Sjåfør',
    description: 'Registrer deg som sjåfør',
    icon: CarIcon,
    color: '#34D186',
  },
  fleetadmin: {
    label: 'Flåteadmin',
    description: 'Administrer flåten din',
    icon: BusinessIcon,
    color: '#f59e0b',
  },
  company: {
    label: 'Transportselskap',
    description: 'Registrer ditt selskap',
    icon: BadgeIcon,
    color: '#3b82f6',
  },
};

const FEATURES = [
  {
    icon: SavingsIcon,
    title: 'Spar penger',
    description: 'Sammenlign priser fra alle leverandører og finn den billigste reisen.',
  },
  {
    icon: SpeedIcon,
    title: 'Rask sammenligning',
    description: 'Få priser fra taxi, Uber, Bolt og VIP på sekunder.',
  },
  {
    icon: SecurityIcon,
    title: 'Trygt og enkelt',
    description: 'Book direkte hos verifiserte leverandører.',
  },
  {
    icon: TrendingIcon,
    title: 'Sanntidspriser',
    description: 'Alltid oppdaterte priser fra alle aktører.',
  },
];

const POPULAR_ROUTES = [
  { from: 'Bergen Lufthavn', to: 'Bergen sentrum', price: 299, provider: 'Rydio' },
  { from: 'Oslo Gardermoen', to: 'Oslo S', price: 489, provider: 'Rydio' },
  { from: 'Stavanger Sola', to: 'Stavanger sentrum', price: 279, provider: 'Rydio' },
];

export default function TurPrisLanding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const firebaseContext = useContext(FirebaseContext);
  const { authRef, RecaptchaVerifier } = firebaseContext;
  const {
    countries,
    requestEmailOtp,
    verifyEmailOtp,
    mobileSignIn,
    googleSignIn,
    passwordSignIn,
    passwordSignUp,
    clearLoginError,
  } = api;

  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);

  // Partner state
  const [partnerCode, setPartnerCode] = useState(null);
  const [partnerData, setPartnerData] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);

  // Auth dialog state
  const [authDialog, setAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login, signup, otp, usertype
  const [selectedUserType, setSelectedUserType] = useState('customer');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Search state
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    country: null,
    selectedCountry: null,
    otp: '',
    verificationId: null,
    entryType: null,
  });

  // Guest booking state
  const [guestBookingDialog, setGuestBookingDialog] = useState(false);
  const [guestBooking, setGuestBooking] = useState({
    name: '',
    phone: '',
    passengers: 1,
    notes: '',
  });
  const [bookingTicket, setBookingTicket] = useState(null);
  const [ticketDialog, setTicketDialog] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const printRef = useRef(null);
  const allowedLangs = ['en', 'nb', 'am', 'ti'];
  const [turprisLang, setTurprisLang] = useState(() => {
    if (typeof window === 'undefined') return 'nb';
    const stored = localStorage.getItem('turprisLang');
    return allowedLangs.includes(stored) ? stored : 'nb';
  });
  const [expandedFooterItem, setExpandedFooterItem] = useState(null);

  const placeholderValues = {
    appName: 'TurPris',
    jobsEmail: 'jobs@turpris.no',
    supportEmail: 'support@turpris.no',
    pressEmail: 'press@turpris.no',
    privacyEmail: 'privacy@turpris.no',
    discountPercent: '10%',
  };

  const applyPlaceholders = (text) =>
    (text || '').replace(/{{(\w+)}}/g, (match, key) => placeholderValues[key] || match);

  const translationKey = turprisLang === 'nb' ? 'no' : turprisLang;
  const currentContent = turprisTranslations[translationKey] || turprisTranslations.en;
  const footerContent = currentContent.footer || turprisTranslations.en.footer;
  const languageOptions = [
    { code: 'nb', label: 'Norsk bokmål' },
    { code: 'en', label: 'English' },
    { code: 'am', label: 'Amharic' },
    { code: 'ti', label: 'Tigrinya' },
  ];

  const pageCopy = {
    no: {
      heroTitle: 'Finn den billigste reisen',
      heroHighlight: 'billigste',
      heroSubtitle: 'Sammenlign priser fra taxi, Uber, Bolt og VIP-tjenester. Spar tid og penger på hver eneste reise.',
      primaryCta: 'Kom i gang gratis',
      secondaryCta: 'Sammenlign priser',
      guestBooking: 'Book uten konto (QR)',
      guestHint: 'Ideell for hoteller og kiosker med termalskriver',
      guestError: 'Velg hentested og destinasjon først',
      guestInfoMissing: 'Navn og telefon er påkrevd',
      partnerBooking: 'Booking via',
      discountLabel: '% rabatt',
      featuresTitle: 'Hvorfor velge TurPris?',
      popularRoutesTitle: 'Populære ruter',
      viewPrices: 'Se priser',
      userTypesTitle: 'For alle typer brukere',
      userTypesSubtitle: 'Enten du er passasjer, sjåfør eller transportselskap – TurPris har noe for deg',
      ctaTitle: 'Klar til å spare på reisen?',
      ctaSubtitle: 'Bli med tusenvis av nordmenn som bruker TurPris for å finne de beste prisene.',
      ctaButton: 'Kom i gang gratis',
      footerTagline: 'Norges smarteste prissammenligner for transport.',
      qrScan: 'Skann QR-koden for å akseptere turen',
      showTicket: 'Vis denne billetten til sjåføren eller skriv ut for gjesten.',
      copyLink: 'Kopier lenke',
      printTicket: 'Skriv ut billett',
      userRegister: 'Registrer deg',
      login: 'Logg inn',
      register: 'Registrer deg',
      loginTitle: 'Logg inn på TurPris',
      loginSubtitle: 'Sammenlign priser og book reiser',
      continueWithGoogle: 'Fortsett med Google',
      orLabel: 'eller',
      emailOrPhone: 'E-post eller telefon',
      passwordLabel: 'Passord',
      createAccount: 'Opprett konto',
      alreadyHaveAccount: 'Har du allerede konto?',
      newUser: 'Ny bruker?',
      loginWithOtp: 'Logg inn med engangskode',
      signupCta: 'Opprett konto',
      firstNameLabel: 'Fornavn',
      lastNameLabel: 'Etternavn',
      emailLabel: 'E-post',
      signupButton: 'Opprett konto',
      searchTitle: 'Hvor skal du?',
      fromPlaceholder: 'Fra: Bergen Lufthavn',
      toPlaceholder: 'Til: Bergen sentrum',
      comparePrices: 'Sammenlign priser',
      guestDialogTitle: 'Book tur uten konto',
      guestDialogDescription: 'Fyll inn informasjonen under for å få en billett med QR-kode som sjåføren kan skanne.',
      routeLabel: 'Rute',
      pickupLabel: 'Fra',
      dropoffLabel: 'Til',
      selectPickup: 'Velg hentested',
      selectDropoff: 'Velg destinasjon',
      estimatedPriceLabel: 'Estimert pris',
      calculatingPrice: 'Beregner pris...',
      priceUnavailable: 'Kunne ikke beregne pris. Velg gyldige adresser.',
      cancel: 'Avbryt',
      createTicket: 'Opprett billett',
      creatingTicket: 'Oppretter...',
      bookingSuccess: 'Booking opprettet!',
      passengerLabel: 'Passasjer',
      phoneLabel: 'Telefon',
      passengersLabel: 'Antall',
      timeLabel: 'Tid',
      passengerCountSuffix: 'person(er)',
      noteLabel: 'Merknad',
      nameLabel: 'Ditt navn',
      phoneInputLabel: 'Telefonnummer',
      passengersInputLabel: 'Antall passasjerer',
      notesInputLabel: 'Merknad (valgfritt)',
      notesPlaceholder: 'F.eks. hotellrom, spesielle behov...',
      bookingHelpTitle: 'Book uten konto (QR)',
      guestErrorMissingRoute: 'Velg hentested og destinasjon først',
      approxLabel: 'ca.',
      pickupLabelLong: 'Hentested',
      dropoffLabelLong: 'Destinasjon',
      printTicketTitle: 'Taxi-bookingbillett',
      scanToAccept: 'Skann for å akseptere tur',
      ticketFooter: 'Vis denne billetten til sjåføren',
      airportTag: 'LUFTHAVN',
      viaLabel: 'via',
      statsSuppliers: 'Leverandører',
      statsSavings: 'Gjennomsnittlig sparing',
      statsRating: 'Brukerrating',
      selectAccountType: 'Velg kontotype',
      otpTitle: 'Skriv inn kode',
      otpSubtitle: 'Vi har sendt en 6-sifret kode til',
      resendIn: 'Send på nytt om',
      resendCode: 'Send kode på nytt',
      confirm: 'Bekreft',
      emailRequired: 'Vennligst skriv inn e-post',
      invalidEmail: 'Ugyldig e-postadresse',
      invalidPhone: 'Vennligst skriv inn gyldig telefonnummer',
      otpRequired: 'Vennligst skriv inn 6-sifret kode',
      fillAllFields: 'Vennligst fyll ut alle felt',
      bookingCreateFailed: 'Kunne ikke opprette booking',
      basePriceLabel: 'Grunnpris',
      serviceFeeLabel: 'Servicetillegg',
      bookingFeeLabel: 'Bestillingsgebyr',
      partnerDiscountLabel: 'Partnerrabatt',
      estimateNote: '*Estimat basert på gjeldende takst',
      startPriceLabel: 'Startpris',
      distancePriceLabel: 'Kjøring',
      timePriceLabel: 'Tid',
      featureCards: [
        { title: 'Spar penger', desc: 'Sammenlign priser fra alle leverandører og finn den billigste reisen.' },
        { title: 'Rask sammenligning', desc: 'Få priser fra taxi, Uber, Bolt og VIP på sekunder.' },
        { title: 'Trygt og enkelt', desc: 'Book direkte hos verifiserte leverandører.' },
        { title: 'Sanntidspriser', desc: 'Alltid oppdaterte priser fra alle aktører.' },
      ],
      popularRoutes: [
        { from: 'Bergen Lufthavn', to: 'Bergen sentrum', price: 299, provider: 'Rydio' },
        { from: 'Oslo Gardermoen', to: 'Oslo S', price: 489, provider: 'Rydio' },
        { from: 'Stavanger Sola', to: 'Stavanger sentrum', price: 279, provider: 'Rydio' },
      ],
      userTypes: {
        customer: { label: 'Passasjer', description: 'Sammenlign priser og book reiser' },
        driver: { label: 'Sjåfør', description: 'Registrer deg som sjåfør' },
        fleetadmin: { label: 'Flåteadmin', description: 'Administrer flåten din' },
        company: { label: 'Transportselskap', description: 'Registrer ditt selskap' },
      },
    },
    en: {
      heroTitle: 'Find the best price',
      heroHighlight: 'best',
      heroSubtitle: 'Compare taxi, Uber, Bolt and VIP. Save time and money on every ride.',
      primaryCta: 'Get started free',
      secondaryCta: 'Compare prices',
      guestBooking: 'Book without account (QR)',
      guestHint: 'Ideal for hotels and kiosks with thermal printers',
      guestError: 'Select pickup and destination',
      guestInfoMissing: 'Name and phone are required',
      partnerBooking: 'Booking via',
      discountLabel: '% off',
      featuresTitle: 'Why choose TurPris?',
      popularRoutesTitle: 'Popular routes',
      viewPrices: 'View prices',
      userTypesTitle: 'For every user type',
      userTypesSubtitle: 'Passenger, driver or operator – TurPris works for you',
      ctaTitle: 'Ready to save on rides?',
      ctaSubtitle: 'Join thousands who use TurPris to find the best fares.',
      ctaButton: 'Get started free',
      footerTagline: 'Norway’s smartest price comparer for transport.',
      qrScan: 'Scan the QR code to accept the ride',
      showTicket: 'Show this ticket to the driver or print for the guest.',
      copyLink: 'Copy link',
      printTicket: 'Print ticket',
      userRegister: 'Sign up',
      login: 'Log in',
      register: 'Sign up',
      loginTitle: 'Log in to TurPris',
      loginSubtitle: 'Compare fares and book rides',
      continueWithGoogle: 'Continue with Google',
      orLabel: 'or',
      emailOrPhone: 'Email or phone',
      passwordLabel: 'Password',
      createAccount: 'Create account',
      alreadyHaveAccount: 'Already have an account?',
      newUser: 'New here?',
      loginWithOtp: 'Log in with one-time code',
      signupCta: 'Create account',
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      emailLabel: 'Email',
      signupButton: 'Create account',
      searchTitle: 'Where are you going?',
      fromPlaceholder: 'From: Bergen Airport',
      toPlaceholder: 'To: Bergen city center',
      comparePrices: 'Compare prices',
      guestDialogTitle: 'Book without account',
      guestDialogDescription: 'Fill in the details below to get a QR ticket the driver can scan.',
      routeLabel: 'Route',
      pickupLabel: 'From',
      dropoffLabel: 'To',
      selectPickup: 'Choose pickup',
      selectDropoff: 'Choose destination',
      estimatedPriceLabel: 'Estimated price',
      calculatingPrice: 'Calculating price...',
      priceUnavailable: 'Could not calculate price. Select valid addresses.',
      cancel: 'Cancel',
      createTicket: 'Create ticket',
      creatingTicket: 'Creating...',
      bookingSuccess: 'Booking created!',
      passengerLabel: 'Passenger',
      phoneLabel: 'Phone',
      passengersLabel: 'Passengers',
      timeLabel: 'Time',
      passengerCountSuffix: 'passenger(s)',
      noteLabel: 'Notes',
      nameLabel: 'Your name',
      phoneInputLabel: 'Phone number',
      passengersInputLabel: 'Number of passengers',
      notesInputLabel: 'Notes (optional)',
      notesPlaceholder: 'E.g. hotel room, special needs...',
      bookingHelpTitle: 'Book without account (QR)',
      guestErrorMissingRoute: 'Select pickup and destination first',
      approxLabel: 'approx.',
      pickupLabelLong: 'Pickup',
      dropoffLabelLong: 'Destination',
      printTicketTitle: 'Taxi booking ticket',
      scanToAccept: 'Scan to accept ride',
      ticketFooter: 'Show this ticket to the driver',
      airportTag: 'AIRPORT',
      viaLabel: 'via',
      statsSuppliers: 'Suppliers',
      statsSavings: 'Average savings',
      statsRating: 'User rating',
      selectAccountType: 'Choose account type',
      otpTitle: 'Enter verification code',
      otpSubtitle: 'We sent a 6-digit code to',
      resendIn: 'Resend in',
      resendCode: 'Resend code',
      confirm: 'Confirm',
      emailRequired: 'Please enter email',
      invalidEmail: 'Invalid email address',
      invalidPhone: 'Please enter a valid phone number',
      otpRequired: 'Please enter the 6-digit code',
      fillAllFields: 'Please fill in all fields',
      bookingCreateFailed: 'Could not create booking',
      basePriceLabel: 'Base fare',
      serviceFeeLabel: 'Service fee',
      bookingFeeLabel: 'Booking fee',
      partnerDiscountLabel: 'Partner discount',
      estimateNote: '*Estimate based on current tariff',
      startPriceLabel: 'Start fare',
      distancePriceLabel: 'Distance',
      timePriceLabel: 'Time',
      featureCards: [
        { title: 'Save money', desc: 'Compare every provider and pick the cheapest ride.' },
        { title: 'Fast comparison', desc: 'Get prices from taxi, Uber, Bolt and VIP in seconds.' },
        { title: 'Safe and simple', desc: 'Book directly with verified providers.' },
        { title: 'Real-time prices', desc: 'Always up-to-date fares from every operator.' },
      ],
      popularRoutes: [
        { from: 'Bergen Airport', to: 'Bergen city center', price: 299, provider: 'Rydio' },
        { from: 'Oslo Gardermoen', to: 'Oslo Central', price: 489, provider: 'Rydio' },
        { from: 'Stavanger Sola', to: 'Stavanger city', price: 279, provider: 'Rydio' },
      ],
      userTypes: {
        customer: { label: 'Passenger', description: 'Compare fares and book rides' },
        driver: { label: 'Driver', description: 'Sign up to drive' },
        fleetadmin: { label: 'Fleet admin', description: 'Manage your fleet' },
        company: { label: 'Transport company', description: 'Register your company' },
      },
    },
    am: {
      heroTitle: 'ተገቢ ዋጋ አግኝ',
      heroHighlight: 'ተመጣጣኝ',
      heroSubtitle: 'ታክሲ፣ Uber፣ Bolt እና VIP አገልግሎቶችን አነጻጽር፣ ጊዜና ገንዘብ ቆጥብ።',
      primaryCta: 'ነጻ ጀምር',
      secondaryCta: 'ዋጋዎችን አነጻጽር',
      guestBooking: 'ያልተመዘገበ ቦኪንግ (QR)',
      guestHint: 'ለሆቴሎችና ለኪዮስኮች ተስማሚ ነው',
      guestError: 'መነሻና መድረሻ ይምረጡ',
      guestInfoMissing: 'ስምና ስልክ ያስፈልጋል',
      partnerBooking: 'ቦኪንግ በ',
      discountLabel: '% ቅናሽ',
      featuresTitle: 'ለምን TurPris?',
      popularRoutesTitle: 'ታዋቂ መንገዶች',
      viewPrices: 'ዋጋዎችን ይመልከቱ',
      userTypesTitle: 'ለሁሉም ተጠቃሚዎች',
      userTypesSubtitle: 'ተጓዥ፣ ሹፌር ወይም ኦፕሬተር – TurPris ይሰራል',
      ctaTitle: 'በጉዞ ዋጋ ለማቆጠብ ዝግጁ ነህ?',
      ctaSubtitle: 'ዋጋ ለማግኘት TurPris የሚጠቀሙ ሺዎችን ተቀላቅሉ።',
      ctaButton: 'ነጻ ጀምር',
      footerTagline: 'የነርዌይ ጉዞ ዋጋ ማነጻጸሪያ ስልት።',
      qrScan: 'ጉዞውን ለማድረስ QR ኮድ ያስክኑ',
      showTicket: 'ይህን ቲኬት ለሹፌር ያሳዩ ወይም ለእንግዶች ይትፉ።',
      copyLink: 'ሊንክ ቅዳ',
      printTicket: 'ቲኬት አትም',
      userRegister: 'ይመዝገቡ',
      login: 'Log in',
      register: 'Sign up',
      loginTitle: 'Log in to TurPris',
      loginSubtitle: 'Compare fares and book rides',
      continueWithGoogle: 'Continue with Google',
      orLabel: 'or',
      emailOrPhone: 'Email or phone',
      passwordLabel: 'Password',
      createAccount: 'Create account',
      alreadyHaveAccount: 'Already have an account?',
      newUser: 'New here?',
      loginWithOtp: 'Log in with one-time code',
      signupCta: 'Create account',
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      emailLabel: 'Email',
      signupButton: 'Create account',
      searchTitle: 'Where are you going?',
      fromPlaceholder: 'From: Bergen Airport',
      toPlaceholder: 'To: Bergen city center',
      comparePrices: 'Compare prices',
      guestDialogTitle: 'Book without account',
      guestDialogDescription: 'Fill in the details below to get a QR ticket the driver can scan.',
      routeLabel: 'Route',
      pickupLabel: 'From',
      dropoffLabel: 'To',
      selectPickup: 'Choose pickup',
      selectDropoff: 'Choose destination',
      estimatedPriceLabel: 'Estimated price',
      calculatingPrice: 'Calculating price...',
      priceUnavailable: 'Could not calculate price. Select valid addresses.',
      cancel: 'Cancel',
      createTicket: 'Create ticket',
      creatingTicket: 'Creating...',
      bookingSuccess: 'Booking created!',
      passengerLabel: 'Passenger',
      phoneLabel: 'Phone',
      passengersLabel: 'Passengers',
      timeLabel: 'Time',
      passengerCountSuffix: 'passenger(s)',
      noteLabel: 'Notes',
      nameLabel: 'Your name',
      phoneInputLabel: 'Phone number',
      passengersInputLabel: 'Number of passengers',
      notesInputLabel: 'Notes (optional)',
      notesPlaceholder: 'E.g. hotel room, special needs...',
      bookingHelpTitle: 'Book without account (QR)',
      guestErrorMissingRoute: 'Select pickup and destination first',
      approxLabel: 'approx.',
      pickupLabelLong: 'Pickup',
      dropoffLabelLong: 'Destination',
      printTicketTitle: 'Taxi booking ticket',
      scanToAccept: 'Scan to accept ride',
      ticketFooter: 'Show this ticket to the driver',
      airportTag: 'AIRPORT',
      viaLabel: 'via',
      statsSuppliers: 'Suppliers',
      statsSavings: 'Average savings',
      statsRating: 'User rating',
      selectAccountType: 'Choose account type',
      otpTitle: 'Enter verification code',
      otpSubtitle: 'We sent a 6-digit code to',
      resendIn: 'Resend in',
      resendCode: 'Resend code',
      confirm: 'Confirm',
      emailRequired: 'Please enter email',
      invalidEmail: 'Invalid email address',
      invalidPhone: 'Please enter a valid phone number',
      otpRequired: 'Please enter the 6-digit code',
      fillAllFields: 'Please fill in all fields',
      bookingCreateFailed: 'Could not create booking',
      basePriceLabel: 'Base fare',
      serviceFeeLabel: 'Service fee',
      bookingFeeLabel: 'Booking fee',
      partnerDiscountLabel: 'Partner discount',
      estimateNote: '*Estimate based on current tariff',
      startPriceLabel: 'Start fare',
      distancePriceLabel: 'Distance',
      timePriceLabel: 'Time',
      featureCards: [
        { title: 'ገንዘብ ያቅፉ', desc: 'ሁሉንም አቅራቢዎች ተከፍተው ዝቅተኛውን ዋጋ ይምረጡ።' },
        { title: 'ፈጣን አነጻጽር', desc: 'ታክሲ፣ Uber፣ Bolt እና VIP ዋጋ በሴኮንድ ያግኙ።' },
        { title: 'የታመነ እና ቀላል', desc: 'በተረጋገጠ አቅራቢ ቀጥታ ይግዙ።' },
        { title: 'በስተጀርባ ትክክለኛ ዋጋ', desc: 'ሁሉንም ኦፕሬተሮች የተዘመነ ዋጋ ያዩ።' },
      ],
      popularRoutes: [
        { from: 'የበርገን አየር ማረፊያ', to: 'የበርገን ከተማ ማእከል', price: 299, provider: 'Rydio' },
        { from: 'ኦስሎ ጋርደሞን', to: 'ኦስሎ ማእከል', price: 489, provider: 'Rydio' },
        { from: 'ስታቫንገር ሶላ', to: 'ስታቫንገር ከተማ', price: 279, provider: 'Rydio' },
      ],
      userTypes: {
        customer: { label: 'ተጓዥ', description: 'ዋጋ አነጻጽር እና ጉዞ ይግዙ' },
        driver: { label: 'ሹፌር', description: 'ለመነዳት ይመዝገቡ' },
        fleetadmin: { label: 'ፍሊት አስተዳዳሪ', description: 'ፍሊትዎን ያስተዳድሩ' },
        company: { label: 'የመጓጓዣ ኩባንያ', description: 'ኩባንያዎን ይመዝገቡ' },
      },
    },
    ti: {
      heroTitle: 'ግቡእ ዋጋ ይርከቡ',
      heroHighlight: 'ተስማሚ',
      heroSubtitle: 'ታክሲ፣ Uber፣ Bolt እና VIP ዋጋታት ኣነጻጽሩ፣ ግዜንን ገንዘብን ድምስሱ።',
      primaryCta: 'ነፃ ጀምር',
      secondaryCta: 'ዋጋታት ኣነጻጽር',
      guestBooking: 'ብዘይ መለለዪ ጉዞ ምዕዳግ (QR)',
      guestHint: 'ንሆቴላትን ኪዮስኮትን ዝምርጽ እዩ',
      guestError: 'መነሻን መድረሻን ምረፅ',
      guestInfoMissing: 'ስምን ቁጽሪ ተለፎን የድልየ',
      partnerBooking: 'ጉዞ ብ',
      discountLabel: '% ቅርፂ',
      featuresTitle: 'ስለምን TurPris?',
      popularRoutesTitle: 'ዝተሓወሱ መንገዶታት',
      viewPrices: 'ዋጋታት ርኣይ',
      userTypesTitle: 'ንኩሎም ዓይነት ተጠቃሚ',
      userTypesSubtitle: 'ስደተኛ፣ ሾፌር ወይ ኦፕሬተር – TurPris ይሰርሕ',
      ctaTitle: 'ናይ ጉዞ ዋጋ ምንካፍ ክትትዕር ዝዓውት ኢኻ?',
      ctaSubtitle: 'ንዋጋ ምምራይ TurPris ዝጥቀሙ ሺዎች ተሳተፉ።',
      ctaButton: 'ነፃ ጀምር',
      footerTagline: 'ናይ ኖርዌይ ዝተኣማማነ መደበኛ ዋጋ ምነጻጸር።',
      qrScan: 'ጉዞ ምቕባል QR ኮድ ይስክኑ',
      showTicket: 'እዚ ቲኬት ሽፈርካ ኣሳእን ወይስ ንግዚኦም አትም።',
      copyLink: 'ሊንክ ቅዳ',
      printTicket: 'ቲኬት አትም',
      userRegister: 'ተመዝገብ',
      login: 'Log in',
      register: 'Sign up',
      loginTitle: 'Log in to TurPris',
      loginSubtitle: 'Compare fares and book rides',
      continueWithGoogle: 'Continue with Google',
      orLabel: 'or',
      emailOrPhone: 'Email or phone',
      passwordLabel: 'Password',
      createAccount: 'Create account',
      alreadyHaveAccount: 'Already have an account?',
      newUser: 'New here?',
      loginWithOtp: 'Log in with one-time code',
      signupCta: 'Create account',
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      emailLabel: 'Email',
      signupButton: 'Create account',
      searchTitle: 'Where are you going?',
      fromPlaceholder: 'From: Bergen Airport',
      toPlaceholder: 'To: Bergen city center',
      comparePrices: 'Compare prices',
      guestDialogTitle: 'Book without account',
      guestDialogDescription: 'Fill in the details below to get a QR ticket the driver can scan.',
      routeLabel: 'Route',
      pickupLabel: 'From',
      dropoffLabel: 'To',
      selectPickup: 'Choose pickup',
      selectDropoff: 'Choose destination',
      estimatedPriceLabel: 'Estimated price',
      calculatingPrice: 'Calculating price...',
      priceUnavailable: 'Could not calculate price. Select valid addresses.',
      cancel: 'Cancel',
      createTicket: 'Create ticket',
      creatingTicket: 'Creating...',
      bookingSuccess: 'Booking created!',
      passengerLabel: 'Passenger',
      phoneLabel: 'Phone',
      passengersLabel: 'Passengers',
      timeLabel: 'Time',
      passengerCountSuffix: 'passenger(s)',
      noteLabel: 'Notes',
      nameLabel: 'Your name',
      phoneInputLabel: 'Phone number',
      passengersInputLabel: 'Number of passengers',
      notesInputLabel: 'Notes (optional)',
      notesPlaceholder: 'E.g. hotel room, special needs...',
      bookingHelpTitle: 'Book without account (QR)',
      guestErrorMissingRoute: 'Select pickup and destination first',
      approxLabel: 'approx.',
      pickupLabelLong: 'Pickup',
      dropoffLabelLong: 'Destination',
      printTicketTitle: 'Taxi booking ticket',
      scanToAccept: 'Scan to accept ride',
      ticketFooter: 'Show this ticket to the driver',
      airportTag: 'AIRPORT',
      viaLabel: 'via',
      statsSuppliers: 'Suppliers',
      statsSavings: 'Average savings',
      statsRating: 'User rating',
      selectAccountType: 'Choose account type',
      otpTitle: 'Enter verification code',
      otpSubtitle: 'We sent a 6-digit code to',
      resendIn: 'Resend in',
      resendCode: 'Resend code',
      confirm: 'Confirm',
      emailRequired: 'Please enter email',
      invalidEmail: 'Invalid email address',
      invalidPhone: 'Please enter a valid phone number',
      otpRequired: 'Please enter the 6-digit code',
      fillAllFields: 'Please fill in all fields',
      bookingCreateFailed: 'Could not create booking',
      basePriceLabel: 'Base fare',
      serviceFeeLabel: 'Service fee',
      bookingFeeLabel: 'Booking fee',
      partnerDiscountLabel: 'Partner discount',
      estimateNote: '*Estimate based on current tariff',
      startPriceLabel: 'Start fare',
      distancePriceLabel: 'Distance',
      timePriceLabel: 'Time',
      featureCards: [
        { title: 'ገንዘብ ኣቕፉ', desc: 'ኩሉ አቅራቢ ኣነጻጽሩ ዝቕረብ ዋጋ ምረፅ።' },
        { title: 'ብፍጥነት አነጻጽር', desc: 'ታክሲ፣ Uber፣ Bolt እና VIP ዋጋታት በፍጥነት ኣግና።' },
        { title: 'ታሪኽ ዘለዎ ዋህዮ', desc: 'ብተረጋገጹ አቅራቢ ቀጥታ ኣዕድግ።' },
        { title: 'ቅርጽ ዋጋ ብዘመን', desc: 'ኩሉ ኦፕሬተሮች ዘለዎ ዋጋ ይቕረብ።' },
      ],
      popularRoutes: [
        { from: 'የበርገን አየር ማረፊያ', to: 'የበርገን ማእከል', price: 299, provider: 'Rydio' },
        { from: 'ኦስሎ ጋርደሞን', to: 'ኦስሎ ማእከል', price: 489, provider: 'Rydio' },
        { from: 'ስታቫንገር ሶላ', to: 'ስታቫንገር ማእከል', price: 279, provider: 'Rydio' },
      ],
      userTypes: {
        customer: { label: 'ተጓዥ', description: 'ዋጋ ምምራይን ጉዞ ምዕዳግን' },
        driver: { label: 'ሾፌር', description: 'ንምንዳብ ተመዝገብ' },
        fleetadmin: { label: 'ፍሊት ኣስተዳዳሪ', description: 'ፍሊትኻ ያስተዳድር' },
        company: { label: 'ናይ መጓጓዣ ኩባንያ', description: 'ኩባንያኻ ይመዝገብ' },
      },
    },
  };

  const copyLang = turprisLang === 'nb' ? 'no' : turprisLang;
  const copy = pageCopy[copyLang] && Object.keys(pageCopy[copyLang]).length > 0 ? pageCopy[copyLang] : pageCopy.en;
  const featuresWithIcons = (copy.featureCards || []).map((item, idx) => ({
    ...item,
    icon: FEATURES[idx]?.icon || SavingsIcon,
  }));
  const routesData = copy.popularRoutes || POPULAR_ROUTES;
  const userTypesWithCopy = { ...USER_TYPES };
  Object.keys(userTypesWithCopy).forEach((key) => {
    if (copy.userTypes?.[key]) {
      userTypesWithCopy[key] = {
        ...userTypesWithCopy[key],
        label: copy.userTypes[key].label,
        description: copy.userTypes[key].description,
      };
    }
  });

  const renderHeroTitle = () => {
    const title = copy.heroTitle || '';
    const highlight = copy.heroHighlight || '';
    if (!highlight) {
      return title;
    }
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedHighlight})`, 'i');
    const parts = title.split(regex);
    if (parts.length === 1) {
      return title;
    }
    return parts.map((part, index) => {
      const isHighlight = part.toLowerCase() === highlight.toLowerCase();
      if (!isHighlight) return <React.Fragment key={`hero-${index}`}>{part}</React.Fragment>;
      return (
        <span
          key={`hero-${index}`}
          style={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {part}
        </span>
      );
    });
  };

  const renderLanguageSelect = (variant) => {
    const isFooter = variant === 'footer';
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <LanguageIcon sx={{ color: isFooter ? 'rgba(255,255,255,0.8)' : '#6b7280', fontSize: 22 }} />
        <FormControl size="small" variant="outlined">
          <Select
            value={turprisLang}
            onChange={(event) => setTurprisLang(event.target.value)}
            sx={{
              minWidth: 160,
              bgcolor: isFooter ? 'rgba(255,255,255,0.08)' : '#fff',
              color: isFooter ? '#fff' : '#111',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isFooter ? 'rgba(255,255,255,0.3)' : '#E5E7EB',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isFooter ? 'rgba(255,255,255,0.5)' : '#d1d5db',
              },
              '& .MuiSvgIcon-root': {
                color: isFooter ? '#fff' : '#6b7280',
              },
            }}
          >
            {languageOptions.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderFooterSection = (sectionKey) => {
    const section = footerContent[sectionKey];
    if (!section) return null;
    const items = Object.entries(section).filter(([k]) => k !== 'sectionTitle');
    return (
      <Grid item xs={12} md={4} key={sectionKey}>
        <Typography fontWeight="bold" mb={2}>
          {section.sectionTitle}
        </Typography>
        {items.map(([key, item]) => {
          const itemId = `${sectionKey}-${key}`;
          const isOpen = expandedFooterItem === itemId;
          return (
            <Box key={key} sx={{ mb: 1 }}>
              <Button
                variant="text"
                onClick={() => setExpandedFooterItem(isOpen ? null : itemId)}
                sx={{
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 0,
                  justifyContent: 'flex-start',
                }}
              >
                {item.title}
              </Button>
              <Collapse in={isOpen}>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, whiteSpace: 'pre-line', mt: 0.5 }}
                >
                  {applyPlaceholders(item.body)}
                </Typography>
              </Collapse>
            </Box>
          );
        })}
      </Grid>
    );
  };

  // Initialize country
  useEffect(() => {
    if (settings && countries) {
      for (let i = 0; i < countries.length; i++) {
        if (countries[i].label === settings.country) {
          setFormData(prev => ({
            ...prev,
            country: countries[i].phone,
            selectedCountry: countries[i],
          }));
          break;
        }
      }
    }
  }, [settings, countries]);

  // Load partner data from URL parameter
  useEffect(() => {
    const code = searchParams.get('partner');
    if (code && firebaseContext?.database) {
      setPartnerCode(code);
      setPartnerLoading(true);

      // Query turpris_partners by partnerCode
      firebaseContext.database.ref('turpris_partners')
        .orderByChild('partnerCode')
        .equalTo(code)
        .once('value')
        .then(snapshot => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const partnerId = Object.keys(data)[0];
            const partner = data[partnerId];
            if (partner.active) {
              setPartnerData({ ...partner, id: partnerId });
            }
          }
          setPartnerLoading(false);
        })
        .catch(err => {
          console.error('Failed to load partner:', err);
          setPartnerLoading(false);
        });
    }
  }, [searchParams, firebaseContext?.database]);

  // Redirect if logged in
  useEffect(() => {
    if (auth.profile?.uid) {
      navigate('/turpris/match');
    }
  }, [auth.profile, navigate]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('turprisLang', turprisLang);
    }
    const targetLang = turprisLang === 'nb' ? 'nb' : turprisLang;
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [turprisLang, i18n]);

  // Handle auth errors
  useEffect(() => {
    if (auth.error?.flag && auth.error?.msg) {
      const errorText = typeof auth.error.msg === 'string'
        ? auth.error.msg
        : (auth.error.msg?.message || auth.error.msg?.error || 'Login error');
      if (errorText !== t('not_logged_in')) {
        setError(errorText);
      }
    }
  }, [auth.error, t]);

  // Handle verification ID from auth
  useEffect(() => {
    if (auth.verificationId && formData.entryType) {
      setFormData(prev => ({ ...prev, verificationId: auth.verificationId }));
      setAuthMode('otp');
    }
  }, [auth.verificationId, formData.entryType]);

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const id = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(id);
    }
  }, [resendTimer]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const openAuthDialog = (mode = 'login') => {
    setAuthDialog(true);
    setAuthMode(mode);
    setError('');
    dispatch(clearLoginError());
  };

  const closeAuthDialog = () => {
    setAuthDialog(false);
    setAuthMode('login');
    setError('');
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      country: formData.country,
      selectedCountry: formData.selectedCountry,
      otp: '',
      verificationId: null,
      entryType: null,
    });
  };

  const handleSearch = () => {
    const originText = origin?.description || '';
    const destText = destination?.description || '';
    if (originText && destText) {
      navigate(`/turpris/match?from=${encodeURIComponent(originText)}&to=${encodeURIComponent(destText)}`);
    } else {
      openAuthDialog('login');
    }
  };

  // Guest booking functions
  const generateBookingId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  // Apply partner pricing adjustments
  const applyPartnerPricing = (basePrice, partner) => {
    if (!partner) return { finalPrice: basePrice, adjustments: null };

    let finalPrice = basePrice;
    const adjustments = {
      basePrice,
      percentageMarkup: 0,
      fixedFee: 0,
      discount: 0,
    };

    // Apply percentage markup
    if (partner.percentageMarkup && partner.percentageMarkup > 0) {
      const markup = basePrice * (partner.percentageMarkup / 100);
      finalPrice += markup;
      adjustments.percentageMarkup = markup;
    }

    // Apply fixed fee
    if (partner.fixedFee && partner.fixedFee > 0) {
      finalPrice += partner.fixedFee;
      adjustments.fixedFee = partner.fixedFee;
    }

    // Apply discount (subtracted from total)
    if (partner.discountPercent && partner.discountPercent > 0) {
      const discount = finalPrice * (partner.discountPercent / 100);
      finalPrice -= discount;
      adjustments.discount = discount;
    }

    adjustments.finalPrice = Math.round(finalPrice);
    return { finalPrice: Math.round(finalPrice), adjustments };
  };

  // Calculate estimated price using Google Maps and backend fare API
  const calculateEstimatedPrice = async (pax = 1) => {
    if (!origin?.coords || !destination?.coords) {
      return null;
    }

    setPriceLoading(true);
    setEstimatedPrice(null);

    try {
      // Use Google Maps Distance Matrix API
      const service = new window.google.maps.DistanceMatrixService();
      const result = await new Promise((resolve, reject) => {
        service.getDistanceMatrix(
          {
            origins: [new window.google.maps.LatLng(origin.coords.lat, origin.coords.lng)],
            destinations: [new window.google.maps.LatLng(destination.coords.lat, destination.coords.lng)],
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (response, status) => {
            if (status === 'OK') {
              resolve(response);
            } else {
              reject(new Error('Distance Matrix failed: ' + status));
            }
          }
        );
      });

      const element = result.rows[0]?.elements[0];
      if (element?.status !== 'OK') {
        throw new Error('No route found');
      }

      const distanceKm = element.distance.value / 1000; // meters to km
      const durationMin = element.duration.value / 60;  // seconds to minutes

      setRouteInfo({
        distance: distanceKm,
        duration: durationMin,
        distanceText: element.distance.text,
        durationText: element.duration.text,
      });

      // Call backend fare calculation API
      const projectId = firebaseContext?.config?.projectId || 'plukk-no';
      const host = `https://us-central1-${projectId}.cloudfunctions.net`;

      const fareResponse = await fetch(`${host}/calculate_fare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          distance_km: distanceKm,
          duration_min: durationMin,
          pax: pax,
          datetime_iso: new Date().toISOString(),
          wheelchair: false,
        }),
      });

      if (fareResponse.ok) {
        const fareData = await fareResponse.json();
        if (fareData?.breakdown?.total) {
          // Apply partner pricing if available
          const { finalPrice, adjustments } = applyPartnerPricing(fareData.breakdown.total, partnerData);

          setEstimatedPrice({
            total: finalPrice,
            baseTotal: fareData.breakdown.total,
            breakdown: fareData.breakdown,
            currency: fareData.currency || 'NOK',
            partnerAdjustments: adjustments,
            partnerName: partnerData?.name,
          });
          return fareData;
        }
      }

      // Fallback: Simple calculation if backend fails
      const now = new Date();
      const dayOfWeek = now.getDay();
      const hour = now.getHours();

      // Simple rate estimation based on fare_rules structure
      let startPrice = 61; // kr (weekday day rate)
      let kmPrice = 14;    // kr per km
      let minPrice = 8;    // kr per minute
      let minFare = 161;   // kr minimum

      // Evening/night surcharges
      if (hour >= 18 || hour < 6) {
        startPrice = hour >= 18 ? 111 : 121;
        kmPrice = hour >= 18 ? 14 : 16;
        minPrice = hour >= 18 ? 8 : 12;
        minFare = hour >= 18 ? 184 : 199;
      }

      // Weekend surcharge
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        startPrice = 121;
        kmPrice = 16;
        minPrice = 12;
        minFare = 222;
      }

      // 5-6 passengers surcharge
      if (pax >= 5) {
        startPrice = Math.round(startPrice * 1.5);
        kmPrice = Math.round(kmPrice * 1.3);
        minFare = Math.round(minFare * 1.4);
      }

      const calculated = startPrice + (kmPrice * distanceKm) + (minPrice * durationMin);
      const baseTotal = Math.max(calculated, minFare);

      // Apply partner pricing if available
      const { finalPrice, adjustments } = applyPartnerPricing(baseTotal, partnerData);

      setEstimatedPrice({
        total: finalPrice,
        baseTotal: Math.round(baseTotal),
        breakdown: {
          base_fare: startPrice,
          distance_component: Math.round(kmPrice * distanceKm),
          time_component: Math.round(minPrice * durationMin),
          total: Math.round(baseTotal),
        },
        currency: 'NOK',
        isEstimate: true,
        partnerAdjustments: adjustments,
        partnerName: partnerData?.name,
      });

      return { total: finalPrice };
    } catch (err) {
      console.error('Price calculation failed:', err);
      setEstimatedPrice(null);
      return null;
    } finally {
      setPriceLoading(false);
    }
  };

  // Calculate price when guest booking dialog opens
  useEffect(() => {
    if (guestBookingDialog && origin?.coords && destination?.coords) {
      calculateEstimatedPrice(guestBooking.passengers);
    }
  }, [guestBookingDialog, origin?.coords, destination?.coords]);

  // Recalculate when passengers change
  useEffect(() => {
    if (guestBookingDialog && origin?.coords && destination?.coords && guestBooking.passengers) {
      calculateEstimatedPrice(guestBooking.passengers);
    }
  }, [guestBooking.passengers]);

  const handleGuestBooking = async () => {
    if (!origin?.description || !destination?.description) {
      setError(copy.guestError);
      return;
    }
    if (!guestBooking.name || !guestBooking.phone) {
      setError(copy.guestInfoMissing);
      return;
    }

    setBookingLoading(true);
    setError('');

    try {
      const bookingId = generateBookingId();
      const bookingTime = new Date().toISOString();
      const bookingData = {
        id: bookingId,
        pickup: {
          address: origin.description,
          coords: origin.coords || null,
        },
        dropoff: {
          address: destination.description,
          coords: destination.coords || null,
        },
        customer: {
          name: guestBooking.name,
          phone: guestBooking.phone,
          isGuest: true,
        },
        passengers: guestBooking.passengers,
        notes: guestBooking.notes,
        status: 'pending',
        createdAt: bookingTime,
        source: partnerData ? 'turpris_partner' : 'turpris_kiosk',
        partner: partnerData ? {
          id: partnerData.id,
          code: partnerCode,
          name: partnerData.name,
          type: partnerData.type,
          commissionPercent: partnerData.commissionPercent || 0,
        } : null,
        estimate: estimatedPrice ? {
          total: estimatedPrice.total,
          baseTotal: estimatedPrice.baseTotal,
          currency: estimatedPrice.currency || 'NOK',
          breakdown: estimatedPrice.breakdown,
          isEstimate: estimatedPrice.isEstimate || false,
          partnerAdjustments: estimatedPrice.partnerAdjustments || null,
        } : null,
        route: routeInfo ? {
          distanceKm: routeInfo.distance,
          durationMin: routeInfo.duration,
          distanceText: routeInfo.distanceText,
          durationText: routeInfo.durationText,
        } : null,
      };

      // Save to Firebase
      if (firebaseContext?.database) {
        await firebaseContext.database.ref(`guest_bookings/${bookingId}`).set(bookingData);
      }

      // Generate booking URL for driver to scan
      const baseUrl = window.location.origin;
      const bookingUrl = `${baseUrl}/driver/accept-ride/${bookingId}`;

      setBookingTicket({
        ...bookingData,
        bookingUrl,
        qrData: bookingUrl,
      });

      setGuestBookingDialog(false);
      setTicketDialog(true);
      setGuestBooking({ name: '', phone: '', passengers: 1, notes: '' });
    } catch (err) {
      setError(`${copy.bookingCreateFailed}: ${err.message}`);
    }

    setBookingLoading(false);
  };

  const handlePrintTicket = () => {
    // Print optimized for LP50 thermal printer (58mm width)
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${copy.printTicketTitle}</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 2mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.3;
            width: 54mm;
            padding: 2mm;
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 4px;
            margin-bottom: 6px;
          }
          .logo {
            font-size: 18px;
            font-weight: bold;
            letter-spacing: 2px;
          }
          .booking-id {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            padding: 6px 0;
            border: 2px solid #000;
            margin: 6px 0;
            letter-spacing: 3px;
          }
          .qr-section {
            text-align: center;
            padding: 8px 0;
          }
          .qr-section svg {
            width: 100px !important;
            height: 100px !important;
          }
          .info-section {
            margin: 6px 0;
          }
          .label {
            font-weight: bold;
            font-size: 9px;
            text-transform: uppercase;
          }
          .value {
            font-size: 11px;
            padding: 2px 0 4px 0;
          }
          .route {
            border: 1px solid #000;
            padding: 4px;
            margin: 4px 0;
          }
          .from-to {
            display: flex;
            align-items: flex-start;
            gap: 4px;
          }
          .dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            margin-top: 2px;
            flex-shrink: 0;
          }
          .green { background: #22c55e; }
          .red { background: #ef4444; }
          .arrow {
            text-align: center;
            font-size: 14px;
            padding: 2px 0;
          }
          .footer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 6px;
            margin-top: 8px;
            font-size: 9px;
          }
          .time {
            font-size: 10px;
            text-align: center;
          }
          .scan-text {
            font-size: 10px;
            font-weight: bold;
            margin-top: 4px;
          }
          .price-box {
            border: 2px solid #000;
            padding: 6px;
            margin: 8px 0;
            text-align: center;
            background: #f0f0f0;
          }
          .price-label {
            font-size: 9px;
            text-transform: uppercase;
          }
          .price-value {
            font-size: 20px;
            font-weight: bold;
            letter-spacing: 1px;
          }
          .price-distance {
            font-size: 9px;
            margin-top: 2px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">RYDIO</div>
          <div style="font-size: 9px;">${copy.printTicketTitle}</div>
        </div>

        <div class="booking-id">${bookingTicket?.id}</div>

        <div class="route">
          <div class="from-to">
            <div class="dot green"></div>
            <div>
              <div class="label">${copy.pickupLabelLong}</div>
              <div class="value">${bookingTicket?.pickup?.address}</div>
            </div>
          </div>
          <div class="arrow">↓</div>
          <div class="from-to">
            <div class="dot red"></div>
            <div>
              <div class="label">${copy.dropoffLabelLong}</div>
              <div class="value">${bookingTicket?.dropoff?.address}</div>
            </div>
          </div>
        </div>

        <div class="info-section">
          <div class="label">${copy.passengerLabel}</div>
          <div class="value">${bookingTicket?.customer?.name}</div>
          <div class="label">${copy.phoneLabel}</div>
          <div class="value">${bookingTicket?.customer?.phone}</div>
          <div class="label">${copy.passengersLabel}</div>
          <div class="value">${bookingTicket?.passengers} ${copy.passengerCountSuffix}</div>
          ${bookingTicket?.notes ? `<div class="label">${copy.noteLabel}</div><div class="value">${bookingTicket.notes}</div>` : ''}
        </div>

        ${bookingTicket?.estimate ? `
        <div class="price-box">
          <div class="price-label">${copy.estimatedPriceLabel}</div>
          <div class="price-value">${bookingTicket.estimate.total} ${bookingTicket.estimate.currency || 'kr'}</div>
          ${bookingTicket.route ? `<div class="price-distance">${bookingTicket.route.distanceText} • ${copy.approxLabel} ${bookingTicket.route.durationText}</div>` : ''}
        </div>
        ` : ''}

        <div class="qr-section">
          ${printContent.querySelector('svg')?.outerHTML || ''}
          <div class="scan-text">${copy.scanToAccept}</div>
        </div>

        <div class="time">${new Date(bookingTicket?.createdAt).toLocaleString(turprisLang === 'nb' ? 'nb-NO' : 'en-US')}</div>

        <div class="footer">
          <div>${copy.ticketFooter}</div>
          <div style="margin-top: 4px;">www.rydio.no</div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleCopyLink = () => {
    if (bookingTicket?.bookingUrl) {
      navigator.clipboard.writeText(bookingTicket.bookingUrl);
    }
  };

  const handleEmailOtp = async () => {
    if (!formData.email) {
      setError(copy.emailRequired);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(copy.invalidEmail);
      return;
    }

    setIsLoading(true);
    setFormData(prev => ({ ...prev, entryType: 'email' }));
    await dispatch(requestEmailOtp(formData.email));
    setResendTimer(30);
    setAuthMode('otp');
    setIsLoading(false);
  };

  const handlePhoneOtp = async () => {
    if (!formData.phone || formData.phone.length < 8) {
      setError(copy.invalidPhone);
      return;
    }

    setIsLoading(true);
    setFormData(prev => ({ ...prev, entryType: 'mobile' }));

    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', {
          size: 'invisible',
        });
      }

      const phoneNumber = '+' + formData.country + formData.phone.replace(/\s/g, '');
      const result = await authRef.signInWithPhoneNumber(phoneNumber, window.recaptchaVerifier);
      setFormData(prev => ({ ...prev, verificationId: result.verificationId }));
      setResendTimer(30);
      setAuthMode('otp');
    } catch (error) {
      setError(error.message);
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      setError(copy.otpRequired);
      return;
    }

    setIsLoading(true);
    try {
      if (formData.entryType === 'email') {
        await dispatch(verifyEmailOtp(formData.email, formData.otp));
      } else {
        await dispatch(mobileSignIn(formData.verificationId, formData.otp));
      }
    } catch (error) {
      setError(error.message);
    }
    setIsLoading(false);
  };

  const handlePasswordLogin = async () => {
    if (!formData.email || !formData.password) {
      setError(copy.fillAllFields);
      return;
    }

    setIsLoading(true);
    await dispatch(passwordSignIn(formData.email, formData.password));
    setIsLoading(false);
  };

  const handlePasswordSignup = async () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError(copy.fillAllFields);
      return;
    }

    setIsLoading(true);
    await dispatch(passwordSignUp(formData.email, formData.password, {
      firstName: formData.firstName,
      lastName: formData.lastName,
      usertype: selectedUserType === 'company' ? 'fleetadmin' : selectedUserType,
    }));
    setIsLoading(false);
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    await dispatch(googleSignIn());
    setIsLoading(false);
  };

  // Render auth dialog content
  const renderAuthContent = () => {
    // User type selection for signup
    if (authMode === 'usertype') {
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" mb={3} textAlign="center">
            {copy.selectAccountType}
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(USER_TYPES).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <Grid item xs={6} key={key}>
                  <Card
                    onClick={() => {
                      setSelectedUserType(key);
                      setAuthMode('signup');
                    }}
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selectedUserType === key ? config.color : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: config.color,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Icon sx={{ fontSize: 40, color: config.color, mb: 1 }} />
                      <Typography fontWeight="bold">{config.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    }

    // OTP verification
    if (authMode === 'otp') {
      return (
        <Box>
          <IconButton onClick={() => setAuthMode('login')} sx={{ mb: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            {copy.otpTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {copy.otpSubtitle} {formData.entryType === 'email' ? formData.email : formData.phone}
          </Typography>

          <TextField
            fullWidth
            name="otp"
            placeholder="000000"
            value={formData.otp}
            onChange={handleInputChange}
            inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: 8, fontSize: 24 } }}
            sx={{ mb: 2 }}
          />

          {resendTimer > 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
              {copy.resendIn} {resendTimer}s
            </Typography>
          ) : (
            <Button
              fullWidth
              variant="text"
              onClick={formData.entryType === 'email' ? handleEmailOtp : handlePhoneOtp}
              sx={{ mb: 2 }}
            >
              {copy.resendCode}
            </Button>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleVerifyOtp}
            disabled={isLoading || formData.otp.length !== 6}
            sx={{
              py: 1.5,
              bgcolor: '#000',
              '&:hover': { bgcolor: '#333' },
              borderRadius: 2,
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : copy.confirm}
          </Button>
        </Box>
      );
    }

    // Signup form
    if (authMode === 'signup') {
      const userConfig = USER_TYPES[selectedUserType];
      return (
        <Box>
          <IconButton onClick={() => setAuthMode('usertype')} sx={{ mb: 2 }}>
            <ArrowBackIcon />
          </IconButton>

          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <Chip
              icon={<userConfig.icon />}
              label={userConfig.label}
              sx={{ bgcolor: userConfig.color + '20', color: userConfig.color }}
            />
          </Box>

          <Typography variant="h6" fontWeight="bold" mb={3}>
            {copy.createAccount}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="firstName"
                label={copy.firstNameLabel}
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                name="lastName"
                label={copy.lastNameLabel}
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            name="email"
            label={copy.emailLabel}
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            name="password"
            label={copy.passwordLabel}
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            sx={{ mt: 2, mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handlePasswordSignup}
            disabled={isLoading}
            sx={{
              py: 1.5,
              bgcolor: '#000',
              '&:hover': { bgcolor: '#333' },
              borderRadius: 2,
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : copy.signupButton}
          </Button>

          <Typography variant="body2" textAlign="center" mt={2}>
            {copy.alreadyHaveAccount}{' '}
            <Button variant="text" onClick={() => setAuthMode('login')}>
              {copy.login}
            </Button>
          </Typography>
        </Box>
      );
    }

    // Login form (default)
    return (
      <Box>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          {copy.loginTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {copy.loginSubtitle}
        </Typography>

        {/* Google Login */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={handleGoogleLogin}
          disabled={isLoading}
          startIcon={<img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" width={20} />}
          sx={{
            py: 1.5,
            mb: 2,
            borderColor: '#E5E7EB',
            color: '#000',
            '&:hover': { bgcolor: '#F9FAFB', borderColor: '#000' },
            borderRadius: 2,
          }}
        >
          {copy.continueWithGoogle}
        </Button>

        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">{copy.orLabel}</Typography>
        </Divider>

        {/* Email/Phone input */}
        <TextField
          fullWidth
          name="email"
          label={copy.emailOrPhone}
          value={formData.email}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          name="password"
          label={copy.passwordLabel}
          type="password"
          value={formData.password}
          onChange={handleInputChange}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handlePasswordLogin}
          disabled={isLoading}
          sx={{
            py: 1.5,
            bgcolor: '#000',
            '&:hover': { bgcolor: '#333' },
            borderRadius: 2,
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : copy.login}
        </Button>

        <Divider sx={{ my: 2 }} />

        <Button
          fullWidth
          variant="text"
          onClick={handleEmailOtp}
          disabled={isLoading || !formData.email}
          sx={{ mb: 1 }}
        >
          {copy.loginWithOtp}
        </Button>

        <Typography variant="body2" textAlign="center" mt={2}>
          {copy.newUser}{' '}
          <Button variant="text" onClick={() => setAuthMode('usertype')}>
            {copy.createAccount}
          </Button>
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      {/* Invisible recaptcha container */}
      <div id="recaptcha-container"></div>

      {/* Header */}
      <Box
        sx={{
          py: 2,
          px: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#fff',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography fontWeight="bold" color="white" fontSize={18}>T</Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Tur<span style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Pris</span>
          </Typography>
          {partnerData && (
            <Chip
              icon={<HotelIcon sx={{ fontSize: 16 }} />}
              label={partnerData.name}
              size="small"
              sx={{
                ml: 2,
                bgcolor: 'rgba(99, 102, 241, 0.1)',
                color: '#6366f1',
                fontWeight: 'medium',
              }}
            />
          )}
        </Box>

        <Box display="flex" gap={{ xs: 1, md: 2 }} flexWrap="wrap" alignItems="center">
          {renderLanguageSelect('header')}
          <Button variant="text" onClick={() => openAuthDialog('login')}>
            {copy.login}
          </Button>
          <Button
            variant="contained"
            onClick={() => openAuthDialog('usertype')}
            sx={{
              bgcolor: '#000',
              '&:hover': { bgcolor: '#333' },
              borderRadius: 2,
            }}
          >
            {copy.register}
          </Button>
        </Box>
      </Box>

      {/* Partner Banner */}
      {partnerData && (
        <Box
          sx={{
            py: 1.5,
            px: 4,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <HotelIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2" fontWeight="medium">
            {copy.partnerBooking} {partnerData.name}
            {partnerData.discountPercent > 0 && (
              <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 4 }}>
                {partnerData.discountPercent}{copy.discountLabel}
              </span>
            )}
          </Typography>
        </Box>
      )}

      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, #fff 0%, #F3F4F6 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                fontWeight="bold"
                sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 3 }}
              >
                {renderHeroTitle()}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                {copy.heroSubtitle}
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => openAuthDialog('usertype')}
                  sx={{
                    py: 1.5,
                    px: 4,
                    bgcolor: '#000',
                    '&:hover': { bgcolor: '#333' },
                    borderRadius: 2,
                  }}
                >
                  {copy.primaryCta}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => openAuthDialog('login')}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderColor: '#000',
                    color: '#000',
                    '&:hover': { bgcolor: '#F9FAFB', borderColor: '#000' },
                    borderRadius: 2,
                  }}
                >
                  {copy.login}
                </Button>
              </Box>

              {/* Quick stats */}
              <Box display="flex" gap={4} mt={4}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">50+</Typography>
                  <Typography variant="body2" color="text.secondary">{copy.statsSuppliers}</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">35%</Typography>
                  <Typography variant="body2" color="text.secondary">{copy.statsSavings}</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">4.8</Typography>
                  <Typography variant="body2" color="text.secondary">{copy.statsRating}</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Search preview card */}
              <Card sx={{ borderRadius: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="bold" mb={3}>
                    {copy.searchTitle}
                  </Typography>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
                    <GoogleMapsAutoComplete
                      placeholder={copy.fromPlaceholder}
                      variant="outlined"
                      value={origin}
                      onChange={(value) => setOrigin(value)}
                      style={{ flex: 1 }}
                    />
                  </Box>
                  <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
                    <GoogleMapsAutoComplete
                      placeholder={copy.toPlaceholder}
                      variant="outlined"
                      value={destination}
                      onChange={(value) => setDestination(value)}
                      style={{ flex: 1 }}
                    />
                  </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                      borderRadius: 2,
                      mb: 1,
                    }}
                  >
                    {copy.comparePrices}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<QrCodeIcon />}
                    onClick={() => {
                      if (origin?.description && destination?.description) {
                        setGuestBookingDialog(true);
                      } else {
                        setError(copy.guestErrorMissingRoute || copy.guestError);
                      }
                    }}
                    sx={{
                      py: 1.5,
                      borderColor: '#6366f1',
                      color: '#6366f1',
                      '&:hover': { borderColor: '#4f46e5', bgcolor: 'rgba(99, 102, 241, 0.04)' },
                      borderRadius: 2,
                    }}
                  >
                    {copy.guestBooking}
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
                    {copy.guestHint}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6}>
          {copy.featuresTitle}
        </Typography>
        <Grid container spacing={4}>
          {featuresWithIcons.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3, borderRadius: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ color: 'white', fontSize: 30 }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" mb={1}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Popular Routes */}
      <Box sx={{ bgcolor: '#fff', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" mb={6}>
            {copy.popularRoutesTitle}
          </Typography>
          <Grid container spacing={3}>
            {routesData.map((route, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' },
                  }}
                  onClick={() => openAuthDialog('login')}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <TaxiIcon sx={{ color: '#6366f1' }} />
                    <Typography variant="caption" color="text.secondary">{copy.airportTag}</Typography>
                  </Box>
                  <Typography fontWeight="bold">{route.from}</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>↓ {route.to}</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        {route.price} kr
                      </Typography>
                      <Typography variant="caption" color="primary">{copy.viaLabel} {route.provider}</Typography>
                    </Box>
                    <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
                      {copy.viewPrices}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* User Types Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight="bold" textAlign="center" mb={2}>
          {copy.userTypesTitle}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={6}>
          {copy.userTypesSubtitle}
        </Typography>
        <Grid container spacing={4}>
          {Object.entries(userTypesWithCopy).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '2px solid transparent',
                    '&:hover': {
                      borderColor: config.color,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => {
                    setSelectedUserType(key);
                    openAuthDialog('usertype');
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      bgcolor: config.color + '15',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 35, color: config.color }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" mb={1}>
                    {config.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {config.description}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: config.color,
                      color: config.color,
                      '&:hover': { bgcolor: config.color + '10' },
                      borderRadius: 2,
                    }}
                  >
                    {copy.userRegister}
                  </Button>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" fontWeight="bold" mb={3}>
            {copy.ctaTitle}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
            {copy.ctaSubtitle}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => openAuthDialog('usertype')}
            sx={{
              py: 2,
              px: 6,
              bgcolor: 'white',
              color: '#6366f1',
              '&:hover': { bgcolor: '#F3F4F6' },
              borderRadius: 2,
              fontSize: 18,
            }}
          >
            {copy.ctaButton}
          </Button>
        </Container>
      </Box>

      {/* Footer with translations */}
      <Box sx={{ bgcolor: '#111', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={4}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography fontWeight="bold" color="white">T</Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">TurPris</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  {copy.footerTagline}
                </Typography>
              </Box>
            </Box>

            {renderLanguageSelect('footer')}
          </Box>

          <Grid container spacing={4}>
            {renderFooterSection('product')}
            {renderFooterSection('company')}
            {renderFooterSection('legal')}
          </Grid>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" sx={{ opacity: 0.5, textAlign: 'center' }}>
            © {new Date().getFullYear()} TurPris. {turprisLang === 'nb' ? 'Alle rettigheter reservert.' : 'All rights reserved.'}
          </Typography>
        </Container>
      </Box>

      {/* Auth Dialog */}
      <Dialog
        open={authDialog}
        onClose={closeAuthDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 2 },
        }}
      >
        <DialogContent>
          <IconButton
            onClick={closeAuthDialog}
            sx={{ position: 'absolute', right: 16, top: 16 }}
          >
            <CloseIcon />
          </IconButton>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {renderAuthContent()}
        </DialogContent>
      </Dialog>

      {/* Guest Booking Dialog */}
      <Dialog
        open={guestBookingDialog}
        onClose={() => setGuestBookingDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TaxiIcon sx={{ color: '#6366f1' }} />
            <Typography variant="h6" fontWeight="bold">
              {copy.guestDialogTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setGuestBookingDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" mb={3}>
            {copy.guestDialogDescription}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              {copy.routeLabel}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
              <Typography variant="body2">
                {origin?.description || copy.selectPickup}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
              <Typography variant="body2">
                {destination?.description || copy.selectDropoff}
              </Typography>
            </Box>
            {routeInfo && (
              <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {routeInfo.distanceText}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {copy.approxLabel} {routeInfo.durationText}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Price Estimation Box */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: priceLoading ? '#f5f5f5' : (estimatedPrice ? 'rgba(99, 102, 241, 0.08)' : '#f5f5f5'),
              border: '1px solid',
              borderColor: estimatedPrice ? 'rgba(99, 102, 241, 0.3)' : '#e0e0e0',
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              {copy.estimatedPriceLabel}
            </Typography>
            {priceLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {copy.calculatingPrice}
                </Typography>
              </Box>
            ) : estimatedPrice ? (
              <Box>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {estimatedPrice.total} {estimatedPrice.currency || 'kr'}
                </Typography>
                {estimatedPrice.partnerAdjustments && estimatedPrice.baseTotal !== estimatedPrice.total && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(34, 197, 94, 0.1)', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {copy.basePriceLabel}: {estimatedPrice.baseTotal} kr
                    </Typography>
                    {estimatedPrice.partnerAdjustments.percentageMarkup > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        + {copy.serviceFeeLabel}: {Math.round(estimatedPrice.partnerAdjustments.percentageMarkup)} kr
                      </Typography>
                    )}
                    {estimatedPrice.partnerAdjustments.fixedFee > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        + {copy.bookingFeeLabel}: {estimatedPrice.partnerAdjustments.fixedFee} kr
                      </Typography>
                    )}
                    {estimatedPrice.partnerAdjustments.discount > 0 && (
                      <Typography variant="caption" color="success.main" display="block" fontWeight="medium">
                        - {copy.partnerDiscountLabel}: -{Math.round(estimatedPrice.partnerAdjustments.discount)} kr
                      </Typography>
                    )}
                  </Box>
                )}
                {estimatedPrice.isEstimate && (
                  <Typography variant="caption" color="text.secondary">
                    {copy.estimateNote}
                  </Typography>
                )}
                {estimatedPrice.breakdown && !estimatedPrice.isEstimate && !estimatedPrice.partnerAdjustments && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {copy.startPriceLabel}: {estimatedPrice.breakdown.base_fare} kr
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {copy.distancePriceLabel}: {Math.round(estimatedPrice.breakdown.distance_component)} kr
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {copy.timePriceLabel}: {Math.round(estimatedPrice.breakdown.time_component)} kr
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {copy.priceUnavailable}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            label={copy.nameLabel}
            value={guestBooking.name}
            onChange={(e) => setGuestBooking({ ...guestBooking, name: e.target.value })}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={copy.phoneInputLabel}
            value={guestBooking.phone}
            onChange={(e) => setGuestBooking({ ...guestBooking, phone: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="+47 XXX XX XXX"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={copy.passengersInputLabel}
            type="number"
            value={guestBooking.passengers}
            onChange={(e) => setGuestBooking({ ...guestBooking, passengers: Math.max(1, parseInt(e.target.value) || 1) })}
            sx={{ mb: 2 }}
            inputProps={{ min: 1, max: 8 }}
          />

          <TextField
            fullWidth
            label={copy.notesInputLabel}
            value={guestBooking.notes}
            onChange={(e) => setGuestBooking({ ...guestBooking, notes: e.target.value })}
            multiline
            rows={2}
            placeholder={copy.notesPlaceholder}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setGuestBookingDialog(false)}>{copy.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleGuestBooking}
            disabled={bookingLoading}
            startIcon={bookingLoading ? <CircularProgress size={20} /> : <QrCodeIcon />}
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
            }}
          >
            {bookingLoading ? copy.creatingTicket : copy.createTicket}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Display Dialog */}
      <Dialog
        open={ticketDialog}
        onClose={() => setTicketDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Typography variant="h5" fontWeight="bold" color="success.main">
            ✓ {copy.bookingSuccess}
          </Typography>
          <IconButton
            onClick={() => setTicketDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            ref={printRef}
            sx={{
              textAlign: 'center',
              p: 3,
              border: '2px dashed #ddd',
              borderRadius: 2,
              bgcolor: '#fafafa',
              mb: 2,
            }}
          >
            <Typography variant="h4" fontWeight="bold" sx={{ letterSpacing: 4, mb: 2 }}>
              {bookingTicket?.id}
            </Typography>

            <QRCodeSVG
              value={bookingTicket?.qrData || ''}
              size={180}
              level="H"
              includeMargin
            />

            <Typography variant="body2" color="text.secondary" mt={1}>
              {copy.qrScan}
            </Typography>

            <Divider sx={{ my: 2 }} />

              <Box textAlign="left">
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">{copy.pickupLabel}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {bookingTicket?.pickup?.address}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">{copy.dropoffLabel}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {bookingTicket?.dropoff?.address}
                    </Typography>
                  </Box>
                </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{copy.passengerLabel}</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {bookingTicket?.customer?.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{copy.phoneLabel}</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {bookingTicket?.customer?.phone}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{copy.passengersLabel}</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {bookingTicket?.passengers} {copy.passengerCountSuffix}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{copy.timeLabel}</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {bookingTicket?.createdAt ? new Date(bookingTicket.createdAt).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </Typography>
                </Grid>
              </Grid>

              {bookingTicket?.notes && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">{copy.noteLabel}</Typography>
                  <Typography variant="body2">{bookingTicket.notes}</Typography>
                </Box>
              )}

              {/* Price Display */}
              {bookingTicket?.estimate && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'rgba(99, 102, 241, 0.1)',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    {copy.estimatedPriceLabel}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {bookingTicket.estimate.total} {bookingTicket.estimate.currency || 'kr'}
                  </Typography>
                  {bookingTicket.route && (
                    <Typography variant="caption" color="text.secondary">
                      {bookingTicket.route.distanceText} • {copy.approxLabel} {bookingTicket.route.durationText}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {copy.showTicket}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'space-between' }}>
          <Button
            startIcon={<CopyIcon />}
            onClick={handleCopyLink}
          >
            {copy.copyLink}
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrintTicket}
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
            }}
          >
            {copy.printTicket}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
